import { z } from 'zod';
import { orgRoleSchema, workspaceRoleSchema } from './permissions';

const timestamp = z.iso.datetime({ offset: true });

/** How long an invitation link works. Mirrors `interval '14 days'` in the invitations migration. */
export const INVITATION_EXPIRY_DAYS = 14;

/** One workspace grant carried by an invitation. `workspaceId: null` means every workspace of the org. */
export const invitationGrantInputSchema = z.object({
  workspaceId: z.uuid().nullable(),
  role: workspaceRoleSchema,
});
export type InvitationGrantInput = z.infer<typeof invitationGrantInputSchema>;

/** Body of `POST /api/orgs/:orgId/invitations`. */
export const createInvitationRequestSchema = z.object({
  email: z.string().trim().toLowerCase().pipe(z.email('Enter a valid email address').max(254)),
  orgRole: orgRoleSchema,
  workspaceGrants: z
    .array(invitationGrantInputSchema)
    .max(100)
    .default([])
    .refine(
      (grants) => new Set(grants.map((g) => g.workspaceId ?? '*')).size === grants.length,
      'One grant per workspace',
    ),
});
export type CreateInvitationRequest = z.infer<typeof createInvitationRequestSchema>;

/** Body of `POST /api/admin/orgs/:orgId/invite-owner` (staff, Q41). */
export const inviteOwnerRequestSchema = createInvitationRequestSchema.pick({ email: true });
export type InviteOwnerRequest = z.infer<typeof inviteOwnerRequestSchema>;

/** The jsonb argument of `create_invitation`. */
export function toRpcGrants(grants: InvitationGrantInput[]) {
  return grants.map((g) => ({ workspace_id: g.workspaceId, role: g.role }));
}

/** What the worker answers after creating or re-sending: never the token, which only travels by email. */
export const createInvitationResponseSchema = z.object({
  invitation: z.object({ id: z.uuid(), email: z.string(), expiresAt: timestamp }),
});
export type CreateInvitationResponse = z.infer<typeof createInvitationResponseSchema>;

export const invitationStatusSchema = z.enum(['pending', 'expired', 'revoked', 'accepted']);
export type InvitationStatus = z.infer<typeof invitationStatusSchema>;

/** A row of `public.invitations_view` (the table without its token hash, plus a computed status). */
export const invitationSchema = z.object({
  id: z.uuid(),
  organization_id: z.uuid(),
  email: z.string(),
  org_role: orgRoleSchema,
  workspace_grants: z.array(
    z.object({ workspace_id: z.uuid().nullable(), role: workspaceRoleSchema }),
  ),
  invited_by: z.uuid().nullable(),
  created_at: timestamp,
  expires_at: timestamp,
  accepted_at: timestamp.nullable(),
  revoked_at: timestamp.nullable(),
  status: invitationStatusSchema,
});
export type Invitation = z.infer<typeof invitationSchema>;

/** A row of `get_invitation(token)`: what the invite page shows whoever holds the link. */
export const invitationLookupSchema = z.object({
  organization_name: z.string(),
  organization_slug: z.string(),
  email: z.string(),
  org_role: orgRoleSchema,
  status: invitationStatusSchema,
  expires_at: timestamp,
});
export type InvitationLookup = z.infer<typeof invitationLookupSchema>;

/** A row of `my_pending_invitations()`: the banner for someone who signed up before following their link. */
export const pendingInvitationSchema = z.object({
  id: z.uuid(),
  organization_name: z.string(),
  org_role: orgRoleSchema,
  invited_by_name: z.string().nullable(),
  expires_at: timestamp,
});
export type PendingInvitation = z.infer<typeof pendingInvitationSchema>;

/** A row returned by `accept_invitation` / `accept_my_invitation`: where to go next. */
export const acceptedInvitationSchema = z.object({
  organization_id: z.uuid(),
  organization_slug: z.string(),
});
export type AcceptedInvitation = z.infer<typeof acceptedInvitationSchema>;
